import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useGetMyCustomEmojisQuery, useDeleteCustomEmojiMutation } from 'store/services/emoji/emojiApi';
import { useIsElite } from 'hooks/useIsElite';
import { CustomEmojiUploadModal } from 'components/CustomEmojiUploadModal/CustomEmojiUploadModal';
import { EliteUpsellPrompt } from 'components/EliteUpsellPrompt/EliteUpsellPrompt';
import RenameEmojiModal from './RenameEmojiModal';
import { AssetUrls } from 'config/assets';
import type { CustomEmoji } from 'types/interfaces/message';
import './CustomEmojiTab.css';

const CustomEmojiTab: React.FC = () => {
  const { isElite } = useIsElite();
  const { data: emojiData, isLoading } = useGetMyCustomEmojisQuery();
  const [deleteCustomEmoji] = useDeleteCustomEmojiMutation();

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadInitialFile, setUploadInitialFile] = useState<File | undefined>(undefined);
  const [renameEmoji, setRenameEmoji] = useState<CustomEmoji | null>(null);
  const [showUpsell, setShowUpsell] = useState(false);

  const emojis = emojiData?.emojis ?? [];
  const activeCount = emojiData?.activeCount ?? 0;
  const maxSlots = emojiData?.maxSlots ?? 10;
  const remainingUploads = maxSlots - activeCount;
  const allSlotsFilled = activeCount >= maxSlots;

  // Lapsed = not elite but has emoji from previous subscription
  const isLapsed = !isElite && emojis.length > 0;
  const canUpload = isElite && !allSlotsFilled;

  const handleEmptySlotClick = useCallback(() => {
    if (isElite) {
      setUploadInitialFile(undefined);
      setUploadModalOpen(true);
    } else {
      setShowUpsell(true);
    }
  }, [isElite]);

  const handleSlotDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isElite) {
      setShowUpsell(true);
      return;
    }
    const file = e.dataTransfer.files[0];
    if (file) {
      setUploadInitialFile(file);
      setUploadModalOpen(true);
    }
  }, [isElite]);

  const handleSlotDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDelete = useCallback(async (emoji: CustomEmoji) => {
    const confirmed = window.confirm(
      `Delete :${emoji.slug}:? This emoji will no longer be available in reactions. Existing reactions using this emoji will show a placeholder.`
    );
    if (!confirmed) return;
    try {
      await deleteCustomEmoji(emoji.slug).unwrap();
      toast.success(`Emoji :${emoji.slug}: deleted`);
    } catch (error: any) {
      toast.error(error.data?.message || 'Failed to delete emoji');
    }
  }, [deleteCustomEmoji]);

  const handleUploadModalClose = useCallback(() => {
    setUploadModalOpen(false);
    setUploadInitialFile(undefined);
  }, []);

  if (isLoading) {
    return <div className="emoji-tab-loading">Loading your emoji...</div>;
  }

  const emptySlotCount = maxSlots - emojis.length;

  return (
    <>
      <div className="emoji-tab-header">
        <h2>Custom Emoji <img src={AssetUrls.icons.elite.badge} alt="Elite" className="emoji-tab-elite-badge" /></h2>
        <p className="emoji-tab-subtitle">
          Upload custom emoji that the entire TradeMagic community can use in reactions.
        </p>
      </div>

      <div className="emoji-slot-grid">
        {emojis.map((emoji) => (
          <div key={emoji.slug} className="emoji-slot emoji-slot--filled">
            <img src={emoji.imageUrl} alt={emoji.name} className="emoji-slot__image" />
            <span className="emoji-slot__name">:{emoji.slug}:</span>
            <div className="emoji-slot__hover-actions">
              {(isElite || isLapsed) && (
                <>
                  {isElite && (
                    <button
                      className="emoji-slot__action-btn"
                      onClick={() => setRenameEmoji(emoji)}
                      title="Rename"
                      type="button"
                    >
                      &#9998;
                    </button>
                  )}
                  <button
                    className="emoji-slot__action-btn emoji-slot__action-btn--delete"
                    onClick={() => handleDelete(emoji)}
                    title="Delete"
                    type="button"
                  >
                    &#128465;
                  </button>
                </>
              )}
            </div>
          </div>
        ))}

        {!allSlotsFilled && Array.from({ length: emptySlotCount }, (_, i) => {
          if (canUpload) {
            return (
              <div
                key={`empty-${i}`}
                className="emoji-slot emoji-slot--empty"
                onClick={handleEmptySlotClick}
                onDrop={handleSlotDrop}
                onDragOver={handleSlotDragOver}
              >
                <span className="emoji-slot__plus-icon">+</span>
                <span className="emoji-slot__upload-text">Upload</span>
              </div>
            );
          }

          // Non-Elite (free or lapsed) — show identical "+" slots that trigger upsell
          return (
            <div
              key={`empty-${i}`}
              className="emoji-slot emoji-slot--empty"
              onClick={handleEmptySlotClick}
              onDrop={handleSlotDrop}
              onDragOver={handleSlotDragOver}
            >
              <span className="emoji-slot__plus-icon">+</span>
              <span className="emoji-slot__upload-text">Upload</span>
            </div>
          );
        })}
      </div>

      <div className="emoji-tab-counter">
        {activeCount} of {maxSlots} emoji uploaded
      </div>

      {allSlotsFilled && (
        <div className="emoji-tab-info">
          All {maxSlots} emoji slots are in use. Delete an existing emoji to upload a new one.
        </div>
      )}

      <CustomEmojiUploadModal
        isOpen={uploadModalOpen}
        onClose={handleUploadModalClose}
        remainingUploads={remainingUploads}
        initialFile={uploadInitialFile}
      />

      <RenameEmojiModal
        isOpen={!!renameEmoji}
        onClose={() => setRenameEmoji(null)}
        emoji={renameEmoji}
      />

      <EliteUpsellPrompt
        isOpen={showUpsell}
        onClose={() => setShowUpsell(false)}
        title="Custom Emoji is an Elite Feature"
        body="Upload custom emoji that the entire community can use in message reactions."
        emojiRow={
          <>
            <span className="elite-upsell-modal__emoji-pill">
              <span>&#128293;</span> 3
            </span>
            <span className="elite-upsell-modal__emoji-pill">
              <span>&#11088;</span> 2
            </span>
            <span className="elite-upsell-modal__emoji-pill">
              <span>&#128077;</span> 1
            </span>
          </>
        }
        source="custom-emoji"
        learnMoreLabel="Upgrade to Elite"
      />
    </>
  );
};

export default CustomEmojiTab;
